import { APP_ORIGIN, appUrl as configuredAppUrl } from './app-origin.js';

export const SESSION_STATUS = Object.freeze({
  UNKNOWN: 'unknown',
  AUTHENTICATED: 'authenticated',
  ANONYMOUS: 'anonymous',
});

const SESSION_PATH = '/api/auth/get-session';
const SESSION_TIMEOUT_MS = 5_000;

function defaultWindow() {
  return typeof window === 'undefined' ? undefined : window;
}

function defaultDocument() {
  return typeof document === 'undefined' ? undefined : document;
}

function isAuthenticatedSession(value) {
  return value !== null
    && typeof value === 'object'
    && value.user !== null
    && typeof value.user === 'object'
    && !Array.isArray(value.user)
    && typeof value.user.id === 'string'
    && value.user.id.trim().length > 0
    && value.session !== null
    && typeof value.session === 'object'
    && !Array.isArray(value.session);
}

function isRedirect(response) {
  return response?.type === 'opaqueredirect'
    || response?.redirected === true
    || (typeof response?.status === 'number' && response.status >= 300 && response.status < 400);
}

/**
 * Small, browser-only session-state store shared by header subscribers. It
 * intentionally remembers only the three status values: the App response is
 * parsed to decide a state and then discarded.
 */
export function createSessionStatusController({
  appOrigin = APP_ORIGIN,
  appUrl = configuredAppUrl,
  fetchImpl = (...args) => fetch(...args),
  getWindow = defaultWindow,
  getDocument = defaultDocument,
  setTimeoutImpl = (...args) => setTimeout(...args),
  clearTimeoutImpl = (...args) => clearTimeout(...args),
  timeoutMs = SESSION_TIMEOUT_MS,
} = {}) {
  let status = SESSION_STATUS.UNKNOWN;
  let activeRequest = null;
  let listening = false;
  const subscribers = new Set();

  function notify() {
    subscribers.forEach((listener) => listener());
  }

  function setStatus(nextStatus) {
    if (status === nextStatus) return;
    status = nextStatus;
    notify();
  }

  function matchingWindow() {
    const browserWindow = getWindow();
    if (!browserWindow?.location?.origin) return null;
    try {
      return browserWindow.location.origin === new URL(appOrigin).origin ? browserWindow : null;
    } catch {
      return null;
    }
  }

  function cancelActiveRequest() {
    if (!activeRequest) return;
    const request = activeRequest;
    activeRequest = null;
    request.cancelled = true;
    clearTimeoutImpl(request.timeout);
    request.controller.abort();
  }

  function timeoutRequest(request) {
    if (activeRequest !== request) return;
    activeRequest = null;
    request.cancelled = true;
    clearTimeoutImpl(request.timeout);
    request.controller.abort();
    setStatus(SESSION_STATUS.UNKNOWN);
  }

  async function requestSession(request) {
    try {
      const response = await fetchImpl(appUrl(SESSION_PATH), {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store',
        redirect: 'manual',
        signal: request.controller.signal,
      });

      if (request.cancelled || activeRequest !== request) return;
      if (isRedirect(response)) {
        setStatus(SESSION_STATUS.UNKNOWN);
        return;
      }
      if (response?.status === 401) {
        setStatus(SESSION_STATUS.ANONYMOUS);
        return;
      }
      if (typeof response?.status !== 'number' || response.status < 200 || response.status >= 300) {
        setStatus(SESSION_STATUS.UNKNOWN);
        return;
      }

      const payload = await response.json();
      if (request.cancelled || activeRequest !== request) return;
      setStatus(payload === null
        ? SESSION_STATUS.ANONYMOUS
        : (isAuthenticatedSession(payload) ? SESSION_STATUS.AUTHENTICATED : SESSION_STATUS.UNKNOWN));
    } catch {
      if (!request.cancelled && activeRequest === request) setStatus(SESSION_STATUS.UNKNOWN);
    } finally {
      clearTimeoutImpl(request.timeout);
      if (activeRequest === request) activeRequest = null;
    }
  }

  function refresh() {
    if (!matchingWindow()) {
      cancelActiveRequest();
      setStatus(SESSION_STATUS.ANONYMOUS);
      return Promise.resolve(status);
    }
    if (activeRequest) return activeRequest.promise;

    setStatus(SESSION_STATUS.UNKNOWN);
    const request = { cancelled: false, controller: new AbortController(), timeout: null, promise: null };
    activeRequest = request;
    request.timeout = setTimeoutImpl(() => timeoutRequest(request), timeoutMs);
    request.promise = requestSession(request).then(() => status);
    return request.promise;
  }

  function onVisibilityChange() {
    if (getDocument()?.visibilityState === 'visible') void refresh();
  }

  function onBrowserReturn() {
    void refresh();
  }

  function startListening() {
    if (listening) return;
    const browserWindow = getWindow();
    const browserDocument = getDocument();
    if (!browserWindow || !browserDocument) return;
    listening = true;
    browserWindow.addEventListener('focus', onBrowserReturn);
    browserWindow.addEventListener('pageshow', onBrowserReturn);
    browserDocument.addEventListener('visibilitychange', onVisibilityChange);
  }

  function stopListening() {
    if (!listening) return;
    const browserWindow = getWindow();
    const browserDocument = getDocument();
    browserWindow?.removeEventListener('focus', onBrowserReturn);
    browserWindow?.removeEventListener('pageshow', onBrowserReturn);
    browserDocument?.removeEventListener('visibilitychange', onVisibilityChange);
    listening = false;
  }

  function subscribe(listener) {
    subscribers.add(listener);
    if (subscribers.size === 1) {
      startListening();
      void refresh();
    }
    return () => {
      subscribers.delete(listener);
      if (subscribers.size === 0) {
        stopListening();
        cancelActiveRequest();
        setStatus(SESSION_STATUS.UNKNOWN);
      }
    };
  }

  return {
    getSnapshot: () => status,
    subscribe,
    refresh,
  };
}
